import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, Select, Typography } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import PropTypes from 'prop-types';

import { useUserContext } from 'src/components/app/context/UserContext';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';
import {
  actionKPISelector,
  actionTargetStatementAtom,
} from 'src/components/planAction/PlanActionDetailPage/components/Action/atoms';
import { useTouchActionPlan } from 'src/components/planAction/PlanActionDetailPage/components/Action/hooks';

import { styles } from './index.styles';

const TargetStatementSelector = ({ actionId, editable = false }) => {
  const { t } = useTranslation('planAction');
  const kpi = useAtomValue(actionKPISelector(actionId));
  const { planActionStatus } = usePlanActionContext();
  const { currentSurvey } = useUserContext();
  const statements = useMemo(() => {
    if (
      !planActionStatus ||
      !planActionStatus.value ||
      !planActionStatus.value.target_statements
    ) {
      return [];
    }
    return planActionStatus.value.target_statements.filter(
      (statement) =>
        statement.kpi_id === kpi && currentSurvey?.year === statement.year,
    );
  }, [kpi, planActionStatus]);

  const [targetStatement, setTargetStatement] = useAtom(
    actionTargetStatementAtom(actionId),
  );
  const { touchActionPlan } = useTouchActionPlan();

  const handleSelect = useCallback(
    (event) => {
      setTargetStatement(Number(event.target.value));
      touchActionPlan();
    },
    [setTargetStatement, touchActionPlan],
  );

  return (
    <div>
      <Typography variant="h6">{t('ActionPlan.SelectStatement')}</Typography>
      <Select
        fullWidth
        size="small"
        name="target-statement"
        value={
          targetStatement !== null && statements.length
            ? String(targetStatement)
            : 'initial'
        }
        onChange={handleSelect}
        disabled={!editable || !kpi}
        sx={styles.selectField}
      >
        <MenuItem value="initial" hidden>
          {t('ActionPlan.TargetStatement')}
        </MenuItem>
        {statements.map((statement) => (
          <MenuItem
            key={statement.id}
            value={String(statement.id)}
            label={statement.label}
          >
            {statement.label}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
};

TargetStatementSelector.propTypes = {
  actionId: PropTypes.number.isRequired,
  editable: PropTypes.bool,
};

export default TargetStatementSelector;
